import { useTypedSelector } from 'src/State/RootReducer'
import useMappedCharts from '../hooks/use-mapped-charts'
import { DateSettings, MappedChartType, SaveChartProps } from 'src/Libs/system-data-handler/types'
import { ChartSettings, CustomLayout, LoadingState, RechartData, SaveChartProps as ChartKitSaveChartProps } from 'src/Libs/chart-kit'
import useModals from 'src/Hooks/useModals'
import useSystemCharts from './use-system-charts'

interface ReturnType {
  customMappedCharts: MappedChartType[]
  handleSaveCustomChart: () => void
  handleSaveDefaultCharts: (props: SaveChartProps) => void
}

/**
 * Hook to manage custom charts: provides custom charts created by the user and logic to save new custom charts or changes about default charts.
 */
const useCustomCharts = (
  defaultAndCustomCharts: CustomLayout<never>[],
  dateSettings: DateSettings,
  data: RechartData,
  chartSettingsList: ChartSettings[],
  saveCustomChart: (props: ChartKitSaveChartProps) => Promise<void>,
  dataLoadingState: LoadingState,
  installationId?: number,
  storageId?: string
): ReturnType => {
  const { openSystemChartEditor } = useModals()
  const { handleSaveCharts } = useSystemCharts({ installationId: installationId ?? 0, storageId })
  const userId = useTypedSelector(state => state.auth.userId)

  const handleSaveCustomChart = (): void => {
    openSystemChartEditor({
      handleSaveChart: (props: ChartSettings) => {
        return saveCustomChart({
          chartId: props.id,
          chartSettings: props,
          chartSettingsList: chartSettingsList,

          userId,
          installationId,
          storageSerialNumber: storageId,
          isInitial: false,
        })
      },
      serialNumber: storageId,
      views: { day: 'line', week: 'line', month: 'bar', year: 'bar', hour: 'bar' },
      loadingState: dataLoadingState,
    })
  }

  const { customMappedCharts } = useMappedCharts({
    handleSaveCharts,
    defaultAndCustomCharts,
    installationId,
    storageId,
    chartSettings: chartSettingsList,
    dateSettings,
    rechartData: data,
  })

  return { customMappedCharts, handleSaveCustomChart, handleSaveDefaultCharts: handleSaveCharts }
}

export default useCustomCharts
